/**
 * Application entry point
 *
 * Startup flow:
 *  1. Load runtime environment configuration from `config.json`
 *     - We build one environment‑agnostic artifact.
 *     - DevOps adds `config.json` to the root of the deployment bucket for each environment.
 *     - This enables changing environment settings without rebuilding the app.
 *  2. Initialize API clients with the loaded configuration.
 *  3. Render the React application.
 */

import './fonts.css';
import './index.css';

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';

import { initApiClients } from './api/clients.ts';
import { QueryProvider } from './api/query/index.tsx';
import { AuthStoreProvider } from './features/auth/store.tsx';
import { getEnvConfig } from './utils/envConfig.ts';
import { Navigation } from './navigation.tsx';

const rootElement = document.getElementById('root');

const renderApp = () => {
  if (!rootElement) {
    throw new Error('Root element not found');
  }
  createRoot(rootElement).render(
    <StrictMode>
      <AuthStoreProvider>
        <QueryProvider>
          <Navigation />
        </QueryProvider>
      </AuthStoreProvider>
    </StrictMode>,
  );
};

const init = async () => {
  try {
    await getEnvConfig();
    initApiClients();
    renderApp();
  } catch (err) {
    console.error('Failed to load environment configuration:', err);
    // Optionally render a fallback error UI here
  }
};

void init();
